import { RefundSearchCriteria } from "domain/entity/Refund/RefundSearchCriteria";
import { RefundConstant } from "presentation/constant/Refund/RefundConstant";
import { useRefundVM } from "presentation/hook/Refund/useRefundVM";
import { useMessageBarVM } from "presentation/hook/useMessageBar";
import { useANAInfoTracked } from "presentation/store/ANAInfo";
import { useRefundTracked } from "presentation/store/Refund/RefundProvider";
import { CriteriaItemContainer } from "presentation/view/components/CriteriaItemContainer";
import DateRangePickerComponent from "presentation/view/components/DateRangePickerComponent";
import { NbisCommonField } from "presentation/view/components/NbisCommonField";
import { useCallback, useMemo, useState } from "react";
import { FieldType, HPHButton, IconButton, IFieldValue, Loader } from "veronica-ui-component/dist/component/core";
import { SidebarActionBar, SidebarActionCross, Sidebarheader, SidebarTitle } from "veronica-ui-component/dist/component/core/styled/uiFramework.styled";

const RefundSearchPanel = () => {
    const SCREEN_CONSTANT = RefundConstant;
    const [refundState] = useRefundTracked();
    const refundVM = useRefundVM();
    const { searchCriteria } = refundState;
    const [isLoading, setIsLoading] = useState(false);
    const messageBarVM = useMessageBarVM();
    const [anaInfoState] = useANAInfoTracked();
    const { operatingUnit } = anaInfoState;

    const resetButtonClicked = useCallback(() => {
        refundVM.onRemoveAllSearchCriteria();
    }, [refundVM]);

    const searchButtonClicked = useCallback((searchCriteria: RefundSearchCriteria) => {
        messageBarVM.showError("");
        setIsLoading(true);
        refundVM.setIsSearchLoading(true);
        refundVM.onSearch(searchCriteria).then((data) => {
            setIsLoading(false);
            refundVM.setIsSearchLoading(false);
        }).catch(error => {
            setIsLoading(false);
            refundVM.setIsSearchLoading(false);
        })

    }, [messageBarVM, refundVM])


    const memoReceiptNo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria?.receiptNo ?? ''}
                fieldValue={searchCriteria?.receiptNo}
                fieldLabel={SCREEN_CONSTANT.RECEIPT_NO}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'receiptNo'}
                maxLength={11}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    refundVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [searchCriteria?.receiptNo, SCREEN_CONSTANT.RECEIPT_NO, refundVM])

    const memoPayOrderNo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria?.payOrderNo ?? ''}
                fieldValue={searchCriteria?.payOrderNo}
                fieldLabel={SCREEN_CONSTANT.PAY_ORDER_NO}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'payOrderNo'}
                maxLength={20}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    refundVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [searchCriteria?.payOrderNo, SCREEN_CONSTANT.PAY_ORDER_NO, refundVM])

    const memoCounterCustomerID = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria?.counterCustomerID ?? ''}
                fieldValue={searchCriteria?.counterCustomerID}
                fieldLabel={SCREEN_CONSTANT.COUNTER_CUSTOMER_ID}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'counterCustomerID'}
                maxLength={60}
                isLimitOptions={true}
                options={refundState.dynamicOptions.companyCodeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    refundVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [searchCriteria?.counterCustomerID, SCREEN_CONSTANT.COUNTER_CUSTOMER_ID, refundState.dynamicOptions.companyCodeDropdownOptions, refundVM])

    const memoRefundType = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria?.refundType ?? ''}
                fieldValue={searchCriteria?.refundType}
                fieldLabel={SCREEN_CONSTANT.REFUND_TYPE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'refundType'}
                maxLength={60}
                options={refundState.dynamicOptions.refundTypeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    refundVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [searchCriteria?.refundType, SCREEN_CONSTANT.REFUND_TYPE, refundState.dynamicOptions.refundTypeDropdownOptions, refundVM])

    const memoStatus = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "470px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria?.status ?? ''}
                fieldValue={searchCriteria?.status}
                fieldLabel={SCREEN_CONSTANT.STATUS}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'status'}
                maxLength={60}
                options={refundState.dynamicOptions.statusDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    refundVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [searchCriteria?.status, SCREEN_CONSTANT.STATUS, refundState.dynamicOptions.statusDropdownOptions, refundVM])


    const memoCreatedDateFromTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "470px", marginBottom: "24px" }}>
            <DateRangePickerComponent
                label={SCREEN_CONSTANT.CREATED_DATE_RANGE}
                width='470px'
                fieldNames={{ startField: "createdDateTimeFrom", endField: "createdDateTimeTo" }}
                dateRange={{ startDate: searchCriteria.createdDateTimeFrom, endDate: searchCriteria.createdDateTimeTo }}
                onDatesChange={refundVM.onDateRangeChange}
            />
        </div>
        , [SCREEN_CONSTANT.CREATED_DATE_RANGE, refundVM.onDateRangeChange, searchCriteria.createdDateTimeFrom, searchCriteria.createdDateTimeTo])

    return (
        <div className='side-form-content-left-wrapper' style={{ padding: "20px 20px 20px 0px" }}>
            <div className={'flex-row'}>
                <div className={'flex-row-item flex-row-item-full-width'}>
                    <Sidebarheader>
                        <SidebarTitle>{SCREEN_CONSTANT.SEARCH_CRITERIA}</SidebarTitle>
                        <SidebarActionCross>
                            <IconButton
                                fileName="Icon-cross" size="medium" toolTipText='Close' toolTipArrow={false} onClick={refundVM.onSearchClick} />
                        </SidebarActionCross>
                    </Sidebarheader>
                </div>
            </div>

            {isLoading && <Loader Indicator="Spinner" size="Medium" />}

            <div className={'add-edit-form'} style={{ maxHeight: '75vh', height: '75vh', overflow: 'auto' }}>
                <div className="flex-row-auto">
                    <div className={'flex-row-item'}>


                        <CriteriaItemContainer>
                            {memoReceiptNo}
                            {(operatingUnit === "SAPT" || operatingUnit === "KICT") ? memoPayOrderNo : memoRefundType}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoCounterCustomerID}
                            {(operatingUnit === "SAPT" || operatingUnit === "KICT") && memoRefundType}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoStatus}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoCreatedDateFromTo}
                        </CriteriaItemContainer>

                    </div>
                </div>
            </div>
            <SidebarActionBar>
                <HPHButton label={'Reset'} size={'Small'} theme={'Secondary'} onClick={resetButtonClicked} />
                <HPHButton label={'Search'} size={'Small'} theme={'Primary'} onClick={() => searchButtonClicked(searchCriteria)} />
            </SidebarActionBar>
        </div>
    );
}

export default RefundSearchPanel;
